import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, MenuItem, TextField,
  Button, CircularProgress, Alert, Tabs, Tab, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, LinearProgress, Autocomplete, Tooltip, IconButton
} from '@mui/material';
import {
  PictureAsPdf, TableChart, Person, Print,
  BarChart as BarChartIcon, TrendingUp, School, Group,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Legend, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, Cell
} from 'recharts';
import { reportsAPI, settingsAPI, studentsAPI, scoresheetAPI, teachersAPI } from '../services/api';
import { exportCSV, exportChartSVG } from '../utils/export';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ── Grade colour helper ────────────────────────────────────────────────────────
const gradeColor = (g) => {
  if (!g || g === '-' || g === 'N/A') return 'default';
  if (g === 'A*' || g === 'A') return 'success';
  if (g === 'B') return 'primary';
  if (g === 'C') return 'info';
  if (g === 'D') return 'warning';
  return 'error';
};

// ── Open a backend PDF report in a new tab ─────────────────────────────────────
const openPDF = (url) => {
  const token = localStorage.getItem('sms_token');
  // Open with auth token via query string (simple approach for HTML reports)
  const sep = url.includes('?') ? '&' : '?';
  window.open(`${API}${url}${sep}token=${token}`, '_blank');
};

// ── Scoresheet inline view ─────────────────────────────────────────────────────
function ScoresheetView({ data, classId, yearId }) {
  if (!data) return null;
  const { subjects, scoresheet, class_average } = data;

  const printScoresheet = () => {
    openPDF(`/reports/pdf/scoresheet?class_id=${classId}&academic_year_id=${yearId}`);
  };

  return (
    <Box>
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:2, flexWrap:'wrap', gap:1 }}>
        <Box sx={{ display:'flex', gap:1.5, flexWrap:'wrap' }}>
          <Chip label={`${scoresheet?.length || 0} students`} color="primary" />
          <Chip label={`${subjects?.length || 0} subjects`} color="secondary" />
          <Chip label={`Class avg: ${class_average ?? '—'}%`} color="success" />
        </Box>
        <Button variant="contained" startIcon={<Print />} onClick={printScoresheet}
          sx={{ bgcolor:'#0f2b4a', '&:hover':{ bgcolor:'#1a3a5c' } }}>
          Print Premium Scoresheet
        </Button>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => {
          const headers = ['#', 'Student', 'Admission', ...subjects.map(s => s.code || s.name), 'Avg %', 'Grade', 'Result'];
          const rows = scoresheet.map((st, i) => [
            i + 1, st.name, st.admission_number,
            ...st.subject_scores.map(sc => sc?.percent != null ? `${sc.percent}%` : '—'),
            st.aggregate != null ? `${st.aggregate}%` : '—',
            st.overall_grade || '—',
            st.result || '—'
          ]);
          exportCSV('class_scoresheet', headers, rows);
        }}>
          Export CSV
        </Button>
      </Box>

      <Card>
        <TableContainer sx={{ overflowX:'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight:800, bgcolor:'#0f2b4a', color:'white', width:40, fontSize:11, position:'sticky', left:0, zIndex:3 }}>#</TableCell>
                <TableCell sx={{ fontWeight:800, bgcolor:'#0f2b4a', color:'white', minWidth:160, fontSize:11, position:'sticky', left:40, zIndex:3 }}>Student</TableCell>
                {subjects?.map(sub => (
                  <TableCell key={sub.id} align="center"
                    sx={{ fontWeight:700, bgcolor:'#0f2b4a', color:'rgba(255,255,255,0.8)', minWidth:52, fontSize:9, p:'6px 3px' }}>
                    <Box sx={{ fontSize:9, fontWeight:800, color:'#c9952a' }}>{sub.code}</Box>
                    <Box sx={{ fontSize:8, color:'rgba(255,255,255,.5)', maxWidth:50, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{sub.name}</Box>
                  </TableCell>
                ))}
                <TableCell align="center" sx={{ fontWeight:800, bgcolor:'#0a1f35', color:'#c9952a', width:72, fontSize:11, borderLeft:'2px solid #c9952a' }}>Avg %</TableCell>
                <TableCell align="center" sx={{ fontWeight:800, bgcolor:'#0a1f35', color:'#c9952a', width:52, fontSize:11 }}>Grade</TableCell>
                <TableCell align="center" sx={{ fontWeight:800, bgcolor:'#0a1f35', color:'#c9952a', width:60, fontSize:11 }}>Result</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scoresheet?.map((row, idx) => {
                const isPassed = parseFloat(row.overall_percent) >= 50;
                return (
                  <TableRow key={row.student_id} hover
                    sx={{ bgcolor: idx % 2 === 0 ? 'white' : '#f7f8fc' }}>
                    <TableCell sx={{ fontWeight:800, fontSize:12, color:'#0f2b4a', position:'sticky', left:0, bgcolor:'inherit', zIndex:1 }}>
                      <Box sx={{
                        width:22, height:22, borderRadius:'50%',
                        display:'inline-flex', alignItems:'center', justifyContent:'center',
                        fontSize:9, fontWeight:800,
                        bgcolor: row.class_rank===1?'#ffd700':row.class_rank===2?'#c0c0c0':row.class_rank===3?'#cd7f32':'#f0f0f0',
                        color: row.class_rank<=3?'#333':'#666',
                        border: row.class_rank<=3?'1px solid #999':'1px solid #ddd'
                      }}>
                        {row.class_rank}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ position:'sticky', left:40, bgcolor:'inherit', zIndex:1 }}>
                      <Typography fontSize={12} fontWeight={700}>{row.last_name}, {row.first_name}</Typography>
                      <Typography fontSize={9} color="text.secondary">{row.admission_number}</Typography>
                    </TableCell>
                    {subjects?.map(sub => {
                      const s = row.subjects?.[sub.id];
                      if (!s?.percent) return (
                        <TableCell key={sub.id} align="center" sx={{ color:'#ddd', fontSize:11, p:'4px 2px' }}>—</TableCell>
                      );
                      return (
                        <TableCell key={sub.id} align="center" sx={{ p:'4px 2px' }}>
                          <Box sx={{ fontSize:11, fontWeight:800, color: s.passed ? '#1a7a3c' : '#c0392b' }}>
                            {parseFloat(s.percent).toFixed(0)}
                          </Box>
                          <Chip label={s.grade} size="small" color={gradeColor(s.grade)}
                            sx={{ height:14, fontSize:8, fontWeight:800, mt:0.2, '& .MuiChip-label':{ px:'4px' } }} />
                        </TableCell>
                      );
                    })}
                    <TableCell align="center" sx={{ borderLeft:'2px solid rgba(201,149,42,0.4)', bgcolor:'rgba(15,43,74,0.03)' }}>
                      <Typography fontWeight={800} fontSize={13}
                        color={isPassed ? 'success.main' : 'error.main'}>
                        {row.overall_percent ?? '—'}%
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ bgcolor:'rgba(15,43,74,0.03)' }}>
                      <Chip label={row.overall_grade || '—'} size="small" color={gradeColor(row.overall_grade)} sx={{ fontWeight:800 }} />
                    </TableCell>
                    <TableCell align="center" sx={{ bgcolor:'rgba(15,43,74,0.03)' }}>
                      <Chip label={isPassed ? 'PASS' : 'FAIL'} size="small"
                        color={isPassed ? 'success' : 'error'}
                        sx={{ fontWeight:800, fontSize:10 }} />
                    </TableCell>
                  </TableRow>
                );
              })}
              {/* Class average footer row */}
              <TableRow sx={{ bgcolor:'#0f2b4a' }}>
                <TableCell colSpan={2} sx={{ color:'rgba(255,255,255,.7)', fontWeight:700, fontSize:11, position:'sticky', left:0, bgcolor:'#0f2b4a', zIndex:1 }}>Class Avg</TableCell>
                {subjects?.map(sub => (
                  <TableCell key={sub.id} align="center"
                    sx={{ color:'rgba(255,255,255,.7)', fontWeight:700, fontSize:11, p:'6px 3px' }}>
                    {/* subject class avg not in data — show dash */}
                    —
                  </TableCell>
                ))}
                <TableCell align="center" sx={{ color:'#c9952a', fontWeight:800, fontSize:14, borderLeft:'2px solid rgba(201,149,42,.5)' }}>
                  {class_average ?? '—'}%
                </TableCell>
                <TableCell colSpan={2} sx={{ color:'rgba(255,255,255,.4)', fontSize:10 }} />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Pass / Fail summary */}
      <Grid container spacing={2} sx={{ mt:2 }}>
        {[
          { label:'Promoted', color:'#1a7a3c', bg:'#e6f4ec', students: scoresheet?.filter(s => parseFloat(s.overall_percent) >= 50) },
          { label:'Failed',   color:'#c0392b', bg:'#fdecea', students: scoresheet?.filter(s => parseFloat(s.overall_percent) < 50) },
        ].map(g => (
          <Grid item xs={12} sm={6} key={g.label}>
            <Card sx={{ borderTop:`4px solid ${g.color}` }}>
              <CardContent sx={{ pb:'12px !important' }}>
                <Typography fontWeight={700} color={g.color} gutterBottom>{g.label} ({g.students?.length || 0})</Typography>
                <Box sx={{ display:'flex', flexWrap:'wrap', gap:0.8 }}>
                  {g.students?.map(s => (
                    <Chip key={s.student_id} size="small"
                      label={`${s.last_name} ${s.first_name} — ${s.overall_percent}%`}
                      sx={{ bgcolor: g.bg, color: g.color, fontWeight:700, fontSize:11 }} />
                  ))}
                  {!g.students?.length && <Typography fontSize={12} color="text.secondary" fontStyle="italic">None</Typography>}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

// ── Student report card inline view ───────────────────────────────────────────
function StudentReportView({ data, studentId, classId, yearId }) {
  if (!data) return null;

  const printReport = () => {
    openPDF(`/reports/pdf/student/${studentId}?class_id=${classId}&academic_year_id=${yearId}`);
  };

  return (
    <Box>
      <Box sx={{ display:'flex', justifyContent:'flex-end', mb:2 }}>
        <Button variant="contained" size="large" startIcon={<Print />} onClick={printReport}
          sx={{ bgcolor:'#0f2b4a', '&:hover':{ bgcolor:'#1a3a5c' } }}>
          Print Premium Report Card
        </Button>
      </Box>

      {/* Student hero card */}
      <Card sx={{ background:'linear-gradient(135deg,#0f2b4a 0%,#1a3a5c 100%)', borderRadius:3, mb:3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Box sx={{ width:64, height:64, borderRadius:2, bgcolor:'rgba(201,149,42,.2)', border:'2px solid #c9952a', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, fontWeight:800, color:'#c9952a', fontFamily:'serif' }}>
                {data.student?.first_name?.[0]}{data.student?.last_name?.[0]}
              </Box>
            </Grid>
            <Grid item xs>
              <Typography variant="h5" fontWeight={800} color="white">
                {data.student?.first_name} {data.student?.last_name}
              </Typography>
              <Typography variant="body2" sx={{ color:'rgba(255,255,255,.55)', mt:0.5 }}>
                {data.student?.admission_number} &nbsp;·&nbsp; {data.class?.name} {data.class?.stream} &nbsp;·&nbsp; {data.academic_year?.year_label} {data.academic_year?.term}
              </Typography>
            </Grid>
            {[
              { label:'Rank', value: data.class_rank ? `#${data.class_rank}` : '—' },
              { label:'Avg %', value: data.overall_percent ? `${parseFloat(data.overall_percent).toFixed(1)}%` : '—' },
              { label:'Grade', value: data.overall_grade || '—' },
              { label:'Result', value: parseFloat(data.overall_percent) >= 50 ? 'PASS' : 'FAIL',
                color: parseFloat(data.overall_percent) >= 50 ? '#4ade80' : '#f87171' },
            ].map(stat => (
              <Grid item key={stat.label} sx={{ textAlign:'center', px:2 }}>
                <Typography variant="h4" fontWeight={800} sx={{ color: stat.color || '#c9952a', fontFamily:'serif' }}>
                  {stat.value}
                </Typography>
                <Typography variant="caption" sx={{ color:'rgba(255,255,255,.45)', textTransform:'uppercase', letterSpacing:1 }}>
                  {stat.label}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Subjects table */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent sx={{ p:0 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor:'#0f2b4a' }}>
                    {['Subject','Mark %','Visual','Grade','Passed'].map(h => (
                      <TableCell key={h} sx={{ color:'rgba(255,255,255,.8)', fontWeight:700, fontSize:10, py:1 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.subjects?.map((sub, i) => (
                    <TableRow key={sub.subject_id} hover sx={{ bgcolor: i%2===0?'white':'#f7f8fc' }}>
                      <TableCell>
                        <Typography fontSize={12} fontWeight={700}>{sub.subject_name}</Typography>
                        {sub.is_core ? <Chip label="Core" size="small" sx={{ height:14, fontSize:8, mt:0.3 }} /> : null}
                      </TableCell>
                      <TableCell sx={{ fontWeight:800, fontSize:13, color: sub.passed ? '#1a7a3c' : '#c0392b' }}>
                        {sub.percent !== null && sub.percent !== undefined ? parseFloat(sub.percent).toFixed(1) : '—'}
                      </TableCell>
                      <TableCell sx={{ minWidth:100 }}>
                        {sub.percent !== null && (
                          <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                            <Box sx={{ flex:1, height:6, bgcolor:'#dde3eb', borderRadius:3, overflow:'hidden', minWidth:50 }}>
                              <Box sx={{ width:`${Math.min(parseFloat(sub.percent)||0,100)}%`, height:'100%', borderRadius:3,
                                bgcolor: parseFloat(sub.percent)>=60?'#1a7a3c':parseFloat(sub.percent)>=45?'#e67e22':'#c0392b' }} />
                            </Box>
                            <Typography fontSize={10} fontWeight={700}>{parseFloat(sub.percent||0).toFixed(0)}%</Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={sub.grade || '—'} size="small" color={gradeColor(sub.grade)} sx={{ fontWeight:800 }} />
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={14} color={sub.passed?'success.main':'error.main'}>
                          {sub.passed !== null && sub.passed !== undefined ? (sub.passed ? '✓' : '✗') : '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        {/* Radar + stats */}
        <Grid item xs={12} md={5}>
          <Card sx={{ mb:2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Subject Performance Radar</Typography>
              <ResponsiveContainer width="100%" height={220}>
                <RadarChart data={data.subjects?.filter(s => s.percent !== null).map(s => ({
                  subject: (s.code || s.subject_name)?.slice(0,7),
                  score: parseFloat(s.percent) || 0,
                  pass: 50
                }))}>
                  <PolarGrid stroke="#dde3eb" />
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize:9, fill:'#4a5568' }} />
                  <Radar name="Score" dataKey="score" stroke="#0f2b4a" fill="#0f2b4a" fillOpacity={0.2} strokeWidth={2} />
                  <Radar name="Pass Line" dataKey="pass" stroke="#c9952a" fill="transparent" strokeDasharray="4 4" strokeWidth={1} />
                  <ChartTooltip formatter={v => `${parseFloat(v).toFixed(1)}%`} />
                  <Legend wrapperStyle={{ fontSize:10 }} />
                </RadarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Mini stat cards */}
          <Grid container spacing={1}>
            {[
              { label:'Subjects Passed', value: data.subjects?.filter(s=>s.passed).length + ' / ' + data.subjects?.length, color:'#1a7a3c' },
              { label:'Total Points', value: data.total_points != null ? parseFloat(data.total_points).toFixed(1) : '—', color:'#0f2b4a' },
              { label:'Class Rank', value: data.class_rank ? `#${data.class_rank}` : '—', color:'#c9952a' },
            ].map(s => (
              <Grid item xs={4} key={s.label}>
                <Card sx={{ textAlign:'center', borderTop:`3px solid ${s.color}` }}>
                  <CardContent sx={{ py:'10px !important' }}>
                    <Typography fontWeight={800} fontSize={15} color={s.color}>{s.value}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize:9, textTransform:'uppercase', letterSpacing:0.5 }}>{s.label}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

// ── Main Reports Component ────────────────────────────────────────────────────
export default function Reports() {
  const { user } = useAuth();
  const isClassTeacher = user?.role === 'class_teacher';
  const isSubjectTeacher = user?.role === 'teacher';
  const isTeacherRole = !!user?.teacher_id && (isClassTeacher || isSubjectTeacher);

  const [tab, setTab] = useState(0);
  const [academicYears, setAcademicYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [homeClasses, setHomeClasses] = useState([]);      // class_teacher's home classes
  const [teachingClasses, setTeachingClasses] = useState([]); // subject teacher's teaching classes
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [scoresheet, setScoresheet] = useState(null);
  const [classReport, setClassReport] = useState(null);
  const [enrolmentReport, setEnrolmentReport] = useState(null);
  const [promotionReport, setPromotionReport] = useState(null);
  const [workloadReport, setWorkloadReport] = useState(null);
  const [studentScoresheet, setStudentScoresheet] = useState(null);

  useEffect(() => {
    settingsAPI.getAcademicYears().then(r => {
      if (r.data.success) {
        setAcademicYears(r.data.data);
        const cur = r.data.data.find(y => y.is_current);
        if (cur) setSelectedYear(cur.id);
      }
    }).catch(() => {});
    settingsAPI.getClasses().then(r => { if (r.data.success) setClasses(r.data.data); }).catch(() => {});
    studentsAPI.getAll({ limit:1000 }).then(r => { if (r.data.success) setStudents(r.data.data); }).catch(() => {});
    // Load teacher-scoped classes
    if (user?.teacher_id) {
      teachersAPI.getMyHomeClasses().then(r => { if (r.data.success) setHomeClasses(r.data.data); }).catch(() => {});
      teachersAPI.getMyAssignments().then(r => {
        if (r.data.success) {
          // unique classes from subject assignments
          const seen = new Set();
          const unique = r.data.data.filter(a => {
            if (seen.has(a.class_id)) return false;
            seen.add(a.class_id); return true;
          });
          setTeachingClasses(unique);
        }
      }).catch(() => {});
    }
  }, []);

  // Auto-select home class for class_teacher
  useEffect(() => {
    if (isClassTeacher && homeClasses.length > 0 && !selectedClass) {
      setSelectedClass(homeClasses[0].id);
    }
  }, [isClassTeacher, homeClasses, selectedClass]);

  const run = async (fn, setter) => {
    setLoading(true); setError('');
    try {
      const r = await fn();
      if (r.data.success) setter(r.data.data);
      else setError(r.data.message || 'Failed to load report');
    } catch (e) {
      setError(e.response?.data?.message || 'Error loading report. Check filters and try again.');
    } finally { setLoading(false); }
  };

  const Filters = ({ showClass=true, showStudent=false, onRun, btnText='Generate' }) => (
    <Card sx={{ mb:3 }}>
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField select label="Academic Year" value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
              {(academicYears.some(y => y.is_current) ? academicYears.filter(y => y.is_current) : academicYears).map(y => (
                <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term} (Current)</MenuItem>
              ))}
            </TextField>
          </Grid>
          {showClass && (
            <Grid item xs={12} sm={3}>
              <TextField select label="Class" value={selectedClass} onChange={e => setSelectedClass(e.target.value)}>
                <MenuItem value="">Select class…</MenuItem>
                {(isClassTeacher
                  ? homeClasses
                  : isSubjectTeacher
                    ? classes.filter(c => teachingClasses.some(t => t.class_id === c.id))
                    : classes
                ).map(c => <MenuItem key={c.id} value={c.id}>{c.name}{c.stream ? ` (${c.stream})` : ''}</MenuItem>)}
              </TextField>
            </Grid>
          )}
          {showStudent && (
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={students}
                getOptionLabel={s => `${s.first_name} ${s.last_name} (${s.admission_number})`}
                onChange={(_, v) => setSelectedStudent(v)}
                renderInput={p => <TextField {...p} label="Student" size="small" />}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={2}>
            <Button variant="contained" onClick={onRun} disabled={loading} fullWidth size="large"
              sx={{ bgcolor:'#0f2b4a', '&:hover':{ bgcolor:'#1a3a5c' }, fontWeight:700 }}>
              {loading ? <CircularProgress size={20} color="inherit" /> : btnText}
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} gutterBottom>Reports Management</Typography>

      <Tabs value={tab} onChange={(_, v) => { setTab(v); setError(''); setScoresheet(null); setStudentScoresheet(null); setClassReport(null); setEnrolmentReport(null); setPromotionReport(null); setWorkloadReport(null); }}
        sx={{ mb:2, borderBottom:1, borderColor:'divider' }}
        variant="scrollable" scrollButtons="auto">
        <Tab icon={<TableChart fontSize="small" />} label="Class Scoresheet" iconPosition="start" />
        <Tab icon={<Person fontSize="small" />} label="Student Report Card" iconPosition="start" />
        <Tab icon={<BarChartIcon fontSize="small" />} label="Class Performance" iconPosition="start" />
        <Tab icon={<Group fontSize="small" />} label="Enrolment" iconPosition="start" />
        <Tab icon={<TrendingUp fontSize="small" />} label="Promotion" iconPosition="start" />
        <Tab icon={<School fontSize="small" />} label="Teacher Workload" iconPosition="start" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb:2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb:2, borderRadius:1 }} />}

      {/* ── SCORESHEET ── */}
      <TabPanel value={tab} index={0}>
        <Filters showClass
          onRun={() => {
            if (!selectedClass) return setError('Please select a class');
            if (!selectedYear) return setError('Please select an academic year');
            run(() => scoresheetAPI.getClass({ class_id: selectedClass, academic_year_id: selectedYear }), setScoresheet);
          }}
        />
        {scoresheet && <ScoresheetView data={scoresheet} classId={selectedClass} yearId={selectedYear} />}
        {!scoresheet && !loading && (
          <Alert severity="info">Select an academic year and class, then click Generate to load the scoresheet.</Alert>
        )}
      </TabPanel>

      {/* ── STUDENT REPORT ── */}
      <TabPanel value={tab} index={1}>
        <Filters showClass showStudent btnText="Load Report Card"
          onRun={() => {
            if (!selectedStudent) return setError('Please select a student');
            if (!selectedClass) return setError('Please select the student\'s class');
            if (!selectedYear) return setError('Please select an academic year');
            run(() => scoresheetAPI.getStudent({
              student_id: selectedStudent.id,
              class_id: selectedClass,
              academic_year_id: selectedYear
            }), setStudentScoresheet);
          }}
        />
        {studentScoresheet && (
          <StudentReportView
            data={studentScoresheet}
            studentId={selectedStudent?.id}
            classId={selectedClass}
            yearId={selectedYear}
          />
        )}
        {!studentScoresheet && !loading && (
          <Alert severity="info">Select a student, their class and academic year, then click Load Report Card.</Alert>
        )}
      </TabPanel>

      {/* ── CLASS PERFORMANCE ── */}
      <TabPanel value={tab} index={2}>
        <Filters showClass
          onRun={() => {
            if (!selectedClass) return setError('Please select a class');
            run(() => reportsAPI.getClassPerformance({ class_id: selectedClass, academic_year_id: selectedYear }), setClassReport);
          }}
        />
        {classReport && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:1 }}>
                    <Typography variant="h6" fontWeight={700}>Subject Performance — Class Average</Typography>
                    <Box sx={{ display:'flex', gap:1 }}>
                      <Tooltip title="Export data as CSV">
                        <IconButton size="small" onClick={() => {
                          const headers = ['Subject', 'Avg %', 'Passed', 'Failed', 'Pass Rate'];
                          const rows = classReport.subject_performance?.map(s => [
                            s.subject_name, parseFloat(s.avg_percent||0).toFixed(1),
                            s.passed||0, s.failed||0,
                            s.total > 0 ? `${Math.round((s.passed/s.total)*100)}%` : '—'
                          ]) || [];
                          exportCSV('class_performance', headers, rows);
                        }}><DownloadIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={classReport.subject_performance?.map(s => ({
                        name: s.subject_name?.slice(0,10) || s.code,
                        avg: parseFloat(s.avg_percent || 0).toFixed(1),
                        passed: s.passed || 0,
                        failed: s.failed || 0
                      }))}
                      margin={{ top:5, right:20, left:0, bottom:35 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{ fontSize:10 }} angle={-30} textAnchor="end" interval={0} />
                      <YAxis tick={{ fontSize:10 }} domain={[0,100]} />
                      <ChartTooltip />
                      <Legend wrapperStyle={{ fontSize:11 }} />
                      <Bar dataKey="avg" name="Avg %" radius={[5,5,0,0]} maxBarSize={48}>
                        {classReport.subject_performance?.map((s, i) => (
                          <Cell key={i} fill={parseFloat(s.avg_percent)>=50?'#0f2b4a':'#c0392b'} />
                        ))}
                      </Bar>
                      <Bar dataKey="passed" name="Passed" fill="#1a7a3c" radius={[5,5,0,0]} maxBarSize={48} />
                      <Bar dataKey="failed" name="Failed" fill="#c0392b" radius={[5,5,0,0]} maxBarSize={48} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {classReport.top_students?.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} gutterBottom>Top 10 Students</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor:'#0f2b4a' }}>
                          <TableCell sx={{ color:'rgba(255,255,255,.8)', fontWeight:700, fontSize:10, w:40 }}>#</TableCell>
                          <TableCell sx={{ color:'rgba(255,255,255,.8)', fontWeight:700, fontSize:10 }}>Student</TableCell>
                          <TableCell sx={{ color:'#c9952a', fontWeight:700, fontSize:10 }}>Average</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {classReport.top_students.map((s, i) => (
                          <TableRow key={i} hover sx={{ bgcolor: i%2===0?'white':'#f7f8fc' }}>
                            <TableCell>
                              <Box sx={{ width:22, height:22, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:800, bgcolor: i===0?'#ffd700':i===1?'#c0c0c0':i===2?'#cd7f32':'#f0f0f0', color:'#333', border:'1px solid #ccc' }}>
                                {i+1}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography fontSize={12} fontWeight={600}>{s.first_name} {s.last_name}</Typography>
                              <Typography fontSize={10} color="text.secondary">{s.admission_number}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography fontSize={13} fontWeight={800} color="success.main">
                                {parseFloat(s.avg_percent).toFixed(1)}%
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </Grid>
            )}

            <Grid item xs={12} md={classReport.top_students?.length > 0 ? 6 : 12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} gutterBottom>Subject Summary</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor:'#0f2b4a' }}>
                        {['Subject','Avg %','High','Low','Passed','Failed'].map(h => (
                          <TableCell key={h} sx={{ color:'rgba(255,255,255,.8)', fontWeight:700, fontSize:10, py:1 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {classReport.subject_performance?.map((s, i) => {
                        const avg = parseFloat(s.avg_percent || 0);
                        return (
                          <TableRow key={i} hover sx={{ bgcolor: i%2===0?'white':'#f7f8fc' }}>
                            <TableCell><Typography fontSize={12} fontWeight={600}>{s.subject_name}</Typography></TableCell>
                            <TableCell>
                              <Chip label={`${avg.toFixed(1)}%`} size="small"
                                color={avg>=50?'success':'error'} sx={{ fontWeight:800 }} />
                            </TableCell>
                            <TableCell sx={{ color:'#1a7a3c', fontWeight:700 }}>{parseFloat(s.max_percent||0).toFixed(1)}%</TableCell>
                            <TableCell sx={{ color:'#c0392b', fontWeight:700 }}>{parseFloat(s.min_percent||0).toFixed(1)}%</TableCell>
                            <TableCell sx={{ color:'#1a7a3c', fontWeight:700 }}>{s.passed || 0}</TableCell>
                            <TableCell sx={{ color:'#c0392b', fontWeight:700 }}>{s.failed || 0}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
        {!classReport && !loading && (
          <Alert severity="info">Select a class and academic year, then click Generate.</Alert>
        )}
      </TabPanel>

      {/* ── ENROLMENT ── */}
      <TabPanel value={tab} index={3}>
        <Filters showClass={false}
          onRun={() => run(() => reportsAPI.getEnrolment({ academic_year_id: selectedYear }), setEnrolmentReport)}
        />
        {enrolmentReport && (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={3}>
              {[
                { label:'Total Enrolled', value: enrolmentReport.summary?.total, color:'#0f2b4a' },
                { label:'Male', value: enrolmentReport.summary?.male, color:'#1565c0' },
                { label:'Female', value: enrolmentReport.summary?.female, color:'#c62828' },
              ].map(s => (
                <Card key={s.label} sx={{ mb:2, borderLeft:`5px solid ${s.color}` }}>
                  <CardContent sx={{ py:'12px !important' }}>
                    <Typography variant="h3" fontWeight={800} color={s.color}>{s.value ?? '—'}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform:'uppercase', letterSpacing:1 }}>{s.label}</Typography>
                  </CardContent>
                </Card>
              ))}
            </Grid>
            <Grid item xs={12} sm={9}>
              <Card>
                <CardContent>
                  <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:1 }}>
                    <Typography variant="h6" fontWeight={700}>Enrolment by Class</Typography>
                    <Tooltip title="Export as CSV">
                      <IconButton size="small" onClick={() => {
                        const headers = ['Class', 'Enrolled', 'Male', 'Female'];
                        const rows = enrolmentReport.by_class?.map(c => [c.class_name, c.total, c.male||0, c.female||0]) || [];
                        exportCSV('enrolment_by_class', headers, rows);
                      }}><DownloadIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </Box>
                  <ResponsiveContainer width="100%" height={Math.max(220, (enrolmentReport.by_class?.length || 1) * 42 + 60)}>
                    <BarChart layout="vertical"
                      data={enrolmentReport.by_class?.map(c => ({
                        name: `${c.class_name}${c.stream ? ' '+c.stream:''}`,
                        Male: c.male || 0, Female: c.female || 0, Total: c.total || 0
                      }))}
                      margin={{ top:5, right:40, left:70, bottom:5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize:10 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize:10 }} width={70} />
                      <ChartTooltip />
                      <Legend wrapperStyle={{ fontSize:11 }} />
                      <Bar dataKey="Male" fill="#1565c0" radius={[0,4,4,0]} maxBarSize={22} />
                      <Bar dataKey="Female" fill="#c62828" radius={[0,4,4,0]} maxBarSize={22} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
        {!enrolmentReport && !loading && <Alert severity="info">Select an academic year and click Generate.</Alert>}
      </TabPanel>

      {/* ── PROMOTION ── */}
      <TabPanel value={tab} index={4}>
        <Filters showClass
          onRun={() => {
            if (!selectedClass) return setError('Please select a class');
            run(() => reportsAPI.getPromotion({ class_id: selectedClass, academic_year_id: selectedYear }), setPromotionReport);
          }}
        />
        {promotionReport && (
          <Grid container spacing={3}>
            {[
              { label:'Total', value: promotionReport.total, color:'#0f2b4a' },
              { label:'Promoted', value: promotionReport.passed, color:'#1a7a3c' },
              { label:'Failed', value: promotionReport.failed, color:'#c0392b' },
              { label:'Pass Rate', value: `${promotionReport.pass_rate}%`, color:'#c9952a' },
            ].map(s => (
              <Grid item xs={6} sm={3} key={s.label}>
                <Card sx={{ textAlign:'center', borderTop:`5px solid ${s.color}` }}>
                  <CardContent sx={{ py:'16px !important' }}>
                    <Typography variant="h3" fontWeight={800} color={s.color}>{s.value}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform:'uppercase', letterSpacing:1 }}>{s.label}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            {[
              { title:`✓ Promoted (${promotionReport.passed})`, color:'#1a7a3c', bg:'#e6f4ec', students: promotionReport.passed_students },
              { title:`✗ Failed (${promotionReport.failed})`, color:'#c0392b', bg:'#fdecea', students: promotionReport.failed_students },
            ].map(g => (
              <Grid item xs={12} sm={6} key={g.title}>
                <Card sx={{ borderTop:`4px solid ${g.color}` }}>
                  <CardContent>
                    <Typography fontWeight={700} color={g.color} gutterBottom>{g.title}</Typography>
                    <Box sx={{ maxHeight:280, overflowY:'auto' }}>
                      {g.students?.map((s, i) => (
                        <Box key={i} sx={{ py:0.8, borderBottom:'1px solid #f0f0f0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                          <Box>
                            <Typography fontSize={13} fontWeight={600}>{s.first_name} {s.last_name}</Typography>
                            <Typography fontSize={10} color="text.secondary">{s.admission_number}</Typography>
                          </Box>
                          <Typography fontSize={13} fontWeight={800} color={g.color}>
                            {parseFloat(s.avg_percent||0).toFixed(1)}%
                          </Typography>
                        </Box>
                      ))}
                      {!g.students?.length && <Typography fontSize={12} fontStyle="italic" color="text.secondary">No students in this group</Typography>}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
        {!promotionReport && !loading && <Alert severity="info">Select a class and academic year. Make sure passing criteria are set for that class first.</Alert>}
      </TabPanel>

      {/* ── TEACHER WORKLOAD ── */}
      <TabPanel value={tab} index={5}>
        <Filters showClass={false}
          onRun={() => run(() => reportsAPI.getTeacherWorkload({ academic_year_id: selectedYear }), setWorkloadReport)}
        />
        {workloadReport && (
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Teacher Workload — {academicYears.find(y=>y.id===selectedYear)?.year_label}</Typography>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor:'#0f2b4a' }}>
                    {['Teacher','Emp. No.','Classes','Subjects','Subjects Assigned','Classes Assigned'].map(h => (
                      <TableCell key={h} sx={{ color:'rgba(255,255,255,.8)', fontWeight:700, fontSize:11 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workloadReport.map((t, i) => (
                    <TableRow key={i} hover sx={{ bgcolor: i%2===0?'white':'#f7f8fc' }}>
                      <TableCell>
                        <Typography fontWeight={700} fontSize={13}>{t.first_name} {t.last_name}</Typography>
                      </TableCell>
                      <TableCell><Chip label={t.employee_number} size="small" variant="outlined" /></TableCell>
                      <TableCell><Chip label={t.classes_count || 0} color="primary" size="small" sx={{ fontWeight:700 }} /></TableCell>
                      <TableCell><Chip label={t.subjects_count || 0} color="secondary" size="small" sx={{ fontWeight:700 }} /></TableCell>
                      <TableCell><Typography fontSize={12} color="text.secondary">{t.subjects || '—'}</Typography></TableCell>
                      <TableCell><Typography fontSize={12} color="text.secondary">{t.classes || '—'}</Typography></TableCell>
                    </TableRow>
                  ))}
                  {!workloadReport.length && (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py:4, color:'text.secondary', fontStyle:'italic' }}>No teacher assignments found for this period</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
        {!workloadReport && !loading && <Alert severity="info">Select an academic year and click Generate.</Alert>}
      </TabPanel>
    </Box>
  );
}
